/**
 * sparks.ts — the shower of sparks thrown off a contact.
 *
 * Two flavours, because two different things are happening. Steel on steel — a
 * block or a parry — throws a wide, bright, fast spray that arcs and dies. Steel
 * on a body throws a shorter, darker, heavier burst. You should be able to tell
 * from the corner of your eye whether your swing was stopped or landed, without
 * reading a damage number.
 *
 * Purely a render effect, like hitstop: it reads the combat events the server
 * already sends and never feeds anything back. The particles live in ONE pooled
 * buffer drawn in a single call, and nothing is allocated per burst — a flurry
 * of exchanges must not start collecting garbage mid-duel.
 */

import * as THREE from 'three';

/** Pool size. Every burst takes from here; the oldest particles are recycled. */
const MAX_PARTICLES = 600;

const GRAVITY = -11;
/** How fast a spark loses speed to the air, per second. */
const DRAG = 2.6;

export type SparkKind = 'steel' | 'flesh' | 'chamber';

interface Profile {
  count: number;
  /** Metres per second, before the intensity scale. */
  speed: number;
  /** Spread of the spray, radians from the contact normal. */
  spread: number;
  lifeMs: number;
  size: number;
  /** Sparks pick a colour between these two, so a burst is not flat. */
  hot: THREE.Color;
  cool: THREE.Color;
}

const PROFILES: Record<SparkKind, Profile> = {
  steel: {
    count: 26,
    speed: 6.5,
    spread: Math.PI * 0.55,
    lifeMs: 420,
    size: 0.05,
    hot: new THREE.Color(0xfff6d0),
    cool: new THREE.Color(0xff9a2e),
  },
  /**
   * Blade on blade at full swing. Deliberately unlike anything else: more
   * sparks, faster, thrown almost flat in a ring rather than up in a plume, and
   * white-hot at the core. A chamber is the loudest thing that can happen in an
   * exchange and it has to be unmistakable from the corner of your eye.
   */
  chamber: {
    count: 46,
    speed: 9.5,
    spread: Math.PI * 0.92,
    lifeMs: 520,
    size: 0.058,
    hot: new THREE.Color(0xffffff),
    cool: new THREE.Color(0xffc44a),
  },
  flesh: {
    count: 14,
    speed: 3.4,
    spread: Math.PI * 0.4,
    lifeMs: 300,
    size: 0.042,
    hot: new THREE.Color(0xff7a52),
    cool: new THREE.Color(0x8e2016),
  },
};

export interface Sparks {
  /**
   * Throws a burst at a world point. `intensity` (0..1) scales how hard it
   * flies — a glancing tap should not look like a full-force parry.
   */
  burst(at: { x: number; y: number; z: number }, kind: SparkKind, intensity: number): void;
  /** Integrates and fades. `now` is render time, so bursts freeze with hitstop. */
  update(now: number): void;
  dispose(): void;
}

export function createSparks(scene: THREE.Scene): Sparks {
  const positions = new Float32Array(MAX_PARTICLES * 3);
  const colors = new Float32Array(MAX_PARTICLES * 3);
  const velocities = new Float32Array(MAX_PARTICLES * 3);
  // Full brightness at birth, zero at death; also the "is this slot free" flag.
  const life = new Float32Array(MAX_PARTICLES);
  const lifeSpan = new Float32Array(MAX_PARTICLES);
  const baseColor = new Float32Array(MAX_PARTICLES * 3);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  // Everything starts dead and off-screen; drawRange keeps it that way cheaply.
  geometry.setDrawRange(0, 0);

  const material = new THREE.PointsMaterial({
    size: PROFILES.steel.size,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  });

  const points = new THREE.Points(geometry, material);
  // Sparks are small and fly past the camera; culling them by a bounding sphere
  // that never updates would blink the whole system out.
  points.frustumCulled = false;
  scene.add(points);

  let cursor = 0;
  let alive = 0;
  let lastUpdate = 0;
  const scratch = new THREE.Color();

  return {
    burst(at, kind, intensity) {
      const p = PROFILES[kind];
      const strength = 0.45 + 0.55 * Math.min(1, Math.max(0, intensity));

      for (let i = 0; i < p.count; i += 1) {
        // Round-robin over the pool: a burst during a heavy exchange steals the
        // oldest sparks rather than being dropped.
        const slot = cursor;
        cursor = (cursor + 1) % MAX_PARTICLES;

        const o = slot * 3;
        positions[o] = at.x;
        positions[o + 1] = at.y;
        positions[o + 2] = at.z;

        // A cone about straight up, widened by the profile's spread. Contacts
        // do not carry a normal, and sparks arcing upward off the point of
        // impact reads correctly for both a parry and a cut.
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.random() * p.spread;
        const speed = p.speed * strength * (0.55 + Math.random() * 0.75);
        velocities[o] = Math.sin(phi) * Math.cos(theta) * speed;
        velocities[o + 1] = Math.cos(phi) * speed;
        velocities[o + 2] = Math.sin(phi) * Math.sin(theta) * speed;

        scratch.copy(p.hot).lerp(p.cool, Math.random());
        baseColor[o] = scratch.r;
        baseColor[o + 1] = scratch.g;
        baseColor[o + 2] = scratch.b;
        colors[o] = scratch.r;
        colors[o + 1] = scratch.g;
        colors[o + 2] = scratch.b;

        const span = p.lifeMs * (0.6 + Math.random() * 0.7);
        life[slot] = span;
        lifeSpan[slot] = span;
      }

      alive = Math.min(MAX_PARTICLES, alive + p.count);
      material.size = p.size;
    },

    update(now) {
      if (lastUpdate === 0) lastUpdate = now;
      // Clamped: a backgrounded tab returns with a huge delta that would fling
      // every live spark to the horizon in one step.
      const dt = Math.min(0.05, Math.max(0, (now - lastUpdate) / 1000));
      lastUpdate = now;
      if (alive === 0 || dt === 0) {
        geometry.setDrawRange(0, alive > 0 ? MAX_PARTICLES : 0);
        return;
      }

      let stillAlive = 0;
      for (let i = 0; i < MAX_PARTICLES; i += 1) {
        if (life[i]! <= 0) continue;
        const remaining = life[i]! - dt * 1000;
        const o = i * 3;
        if (remaining <= 0) {
          life[i] = 0;
          // Dead sparks are not removed from the buffer, only blacked out —
          // additive blending draws black as nothing.
          colors[o] = 0;
          colors[o + 1] = 0;
          colors[o + 2] = 0;
          continue;
        }
        life[i] = remaining;
        stillAlive += 1;

        const decay = Math.max(0, 1 - DRAG * dt);
        velocities[o] = velocities[o]! * decay;
        velocities[o + 1] = velocities[o + 1]! * decay + GRAVITY * dt;
        velocities[o + 2] = velocities[o + 2]! * decay;

        positions[o] = positions[o]! + velocities[o]! * dt;
        positions[o + 1] = positions[o + 1]! + velocities[o + 1]! * dt;
        positions[o + 2] = positions[o + 2]! + velocities[o + 2]! * dt;

        // Fade on a curve rather than linearly: sparks stay bright and then go
        // out, which is what a cooling ember actually does.
        const t = remaining / (lifeSpan[i]! || 1);
        const fade = t * t;
        colors[o] = baseColor[o]! * fade;
        colors[o + 1] = baseColor[o + 1]! * fade;
        colors[o + 2] = baseColor[o + 2]! * fade;
      }

      alive = stillAlive;
      geometry.setDrawRange(0, stillAlive > 0 ? MAX_PARTICLES : 0);
      geometry.attributes['position']!.needsUpdate = true;
      geometry.attributes['color']!.needsUpdate = true;
    },

    dispose() {
      scene.remove(points);
      geometry.dispose();
      material.dispose();
    },
  };
}
